import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import Seo from "@/components/Seo";
import { getAuthor } from "@/data/authors";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Faq from "@/components/landing/Faq";
import FallbackBanner from "@/components/landing/FallbackBanner";
import TryAppPopup from "@/components/blog/TryAppPopup";

/**
 * Renders a paragraph that may contain markdown-style links [text](url).
 * Used so blog content generated by the LLM can include internal links to
 * cornerstone pages.
 */
const MarkdownParagraph = ({ text, className }) => {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const isExternal = /^https?:\/\//.test(m[2]);
    parts.push(
      isExternal ? (
        <a key={m.index} href={m[2]} className="border-b border-[hsl(var(--forest))]/40 text-[hsl(var(--forest))] hover:border-[hsl(var(--forest))]" target="_blank" rel="noopener noreferrer">
          {m[1]}
        </a>
      ) : (
        <Link key={m.index} to={m[2]} className="border-b border-[hsl(var(--forest))]/40 text-[hsl(var(--forest))] hover:border-[hsl(var(--forest))]">
          {m[1]}
        </Link>
      )
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <p className={className}>{parts}</p>;
};

/**
 * ArticlePage — renders a kennisbank or programmatic SEO article
 * from a config object: { slug, category, title, lead, datePublished, sections[], faq[], related[] }
 */
export const ArticlePage = ({
  article,
  seo,
  basePath,
  relatedBasePath,
  breadcrumbs,
  relatedAll = {},
  isFallback = false,
}) => {
  const { t } = useTranslation();
  if (!article) return null;

  return (
    <main data-testid={`article-${article.slug}`} className="bg-background">
      <Seo
        title={seo?.title ?? article.title}
        description={seo?.description ?? article.lead}
        path={`${basePath}/${article.slug}`}
        keywords={seo?.keywords}
        image={seo?.image ?? article.heroImage}
        type="article"
        breadcrumbs={breadcrumbs}
        faq={article.faq}
        article={{
          headline: article.title,
          datePublished: article.datePublished,
          dateModified: article.dateModified ?? article.datePublished,
          authorSlug: article.author,
          image: article.heroImage,
        }}
        noindex={isFallback}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />
      {isFallback && <FallbackBanner slug={article.slug} basePath={basePath} />}

      <article className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          {article.category}
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
          {article.title}
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
          {article.lead}
        </p>
        {article.datePublished && (
          <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            {t("blog.published")}: {new Date(article.datePublished).toLocaleDateString()}
          </p>
        )}

        {(() => {
          const author = getAuthor(article.author);
          return (
            <div
              data-testid={`article-author-${article.slug}`}
              className="mt-6 flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-4"
            >
              <img
                src={author.image}
                alt={author.name}
                width={48}
                height={48}
                className="h-12 w-12 flex-none rounded-full object-cover ring-2 ring-[hsl(var(--border))]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="text-sm">
                <p className="font-semibold text-[hsl(var(--ink))]">{author.name}</p>
                <p className="text-xs text-[hsl(var(--ink-soft))]">
                  {author.jobTitle} · {author.affiliation}
                </p>
              </div>
            </div>
          );
        })()}

        {article.heroImage && (
          <figure
            data-testid={`article-hero-${article.slug}`}
            className="mt-10 overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]"
          >
            <picture>
              {/* AVIF source — falls back to WebP if browser doesn't support AVIF.
                  Same file path, just swapping the extension (see convert_blog_images_to_avif.py). */}
              {article.heroImage.endsWith(".webp") && (
                <source
                  type="image/avif"
                  srcSet={article.heroImage.replace(/\.webp$/, ".avif")}
                />
              )}
              <img
                src={article.heroImage}
                alt={`${article.title} — illustratie bij EquiSight ${article.category || "artikel"}`}
                loading="lazy"
                width={1376}
                height={768}
                className="block aspect-[16/9] w-full object-cover"
              />
            </picture>
          </figure>
        )}

        <div className="mt-12 space-y-12 text-[hsl(var(--ink))]">
          {(article.sections ?? []).map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-2xl font-medium leading-snug sm:text-3xl">
                {s.h}
              </h2>
              {s.p && (
                <MarkdownParagraph
                  text={s.p}
                  className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]"
                />
              )}
              {s.list && (
                <ul className="mt-4 space-y-2 text-[hsl(var(--ink-soft))]">
                  {s.list.map((li) => (
                    <li key={li} className="flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--sand-dark))]" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Disclaimer */}
        {(article.category === "Aandoeningen" ||
          article.category === "Gezondheid" ||
          article.category === "Voeding" ||
          article.category === "Hoefverzorging") && (
          <div className="mt-12 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-6 text-sm text-[hsl(var(--ink-soft))]">
            <strong className="text-[hsl(var(--ink))]">{t("blog.disclaimerLabel")}</strong>{" "}
            {t("blog.disclaimer")}
          </div>
        )}
      </article>

      {/* Cornerstone CTA — shown on supporting blogs that link to a guide page */}
      {article.cornerstone_link && (
        <section data-testid="cornerstone-cta" className="mx-auto max-w-3xl px-6 pb-4 lg:px-10">
          <div className="rounded-2xl border border-[hsl(var(--forest))]/20 bg-[hsl(var(--forest))]/5 p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--forest))]">
              Volledige gids
            </p>
            <p className="mt-2 font-display text-xl text-[hsl(var(--ink))]">
              Wil je dit dieper uitpluizen? Bekijk onze {article.cornerstone_link.label}.
            </p>
            <Link
              to={article.cornerstone_link.path}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
            >
              Ga naar de gids <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {article.faq && article.faq.length > 0 && <Faq items={article.faq} />}

      {/* Related articles */}
      {article.related && article.related.length > 0 && (
        <section className="bg-[hsl(var(--surface-secondary))] py-16">
          <div className="mx-auto max-w-3xl px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              {t("blog.related")}
            </p>
            <ul className="mt-6 space-y-3">
              {article.related.map((slug) => {
                const rel = relatedAll[slug];
                if (!rel) return null;
                return (
                  <li key={slug}>
                    <Link
                      to={`${relatedBasePath ?? basePath}/${slug}`}
                      className="group inline-flex items-center gap-2 text-[hsl(var(--ink))]"
                    >
                      <span className="border-b border-[hsl(var(--ink))]/30 group-hover:border-[hsl(var(--ink))]">
                        {rel.title}
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      <FinalCTA />
      <Footer />
      <TryAppPopup ctaHref="/#waitlist" source="blog" />
    </main>
  );
};

export default ArticlePage;
